/**
 * <TutorProgramChapterRow> — un chapitre d'un programme de révision, vu par le tuteur (molecule).
 *
 * La carte de chapitre que le legacy montre au tuteur (`StudyItemComponent`, `showChapterProgress`)
 * : le picto de la matière, le chapitre parent en petit, le chapitre, et la progression de l'élève
 * sur ce chapitre (sa barre, et son pourcentage pour qui ne voit pas la couleur). Quand le tuteur
 * choisit un chapitre pour générer une activité (`onPress`), toute la ligne presse, avec son
 * chevron ; sinon elle se lit.
 *
 * Elle ne dessine pas de carte : une journée du programme est UNE carte-liste.
 */
import React from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { ProgressBar } from "../../atoms/ProgressBar"
import { SubjectBadge } from "../../atoms/SubjectBadge"
import { Text } from "../../atoms/typography/Text"
import { CardListRow } from "../CardList"
import { type UITutorProgramChapter } from "../../../ports/primary/ui/types/tutorEleves"

export type TutorProgramChapterRowProps = {
    chapter: UITutorProgramChapter
    /** Choisir ce chapitre. Absent : la ligne se lit seulement. */
    onPress?: () => void
    divided?: boolean
    testID?: string
}

export function TutorProgramChapterRow(props: TutorProgramChapterRowProps) {
    const { chapter, onPress, divided = false, testID } = props
    const progress = chapter.progress != null ? Math.max(0, Math.min(100, Math.round(chapter.progress))) : null

    const line = (
        <CardListRow divided={divided}>
            <Row gap="3" align="center">
                <SubjectBadge subject={chapter.subject} />
                <Stack gap="1" style={styles.body}>
                    {chapter.parentName != null && chapter.parentName !== "" && (
                        <Text size="text-sm" color="text-muted" numberOfLines={1}>
                            {chapter.parentName}
                        </Text>
                    )}
                    <Text size="text-md" weight="semibold">
                        {chapter.name}
                    </Text>
                    {progress != null && (
                        <Row gap="2" align="center">
                            <View style={styles.bar}>
                                <ProgressBar value={progress} color={progress >= 100 ? "success" : "role"} />
                            </View>
                            <Text size="text-sm" color="text-muted">
                                {`${progress} %`}
                            </Text>
                        </Row>
                    )}
                </Stack>
                {onPress != null && <Icon name="chevron-right" size={16} color="text-muted" />}
            </Row>
        </CardListRow>
    )

    if (onPress == null) return <View testID={testID}>{line}</View>
    return (
        <Pressable
            onPress={onPress}
            accessibilityRole="button"
            accessibilityLabel={`${chapter.name}${progress != null ? `, ${progress} %` : ""}`}
            style={({ pressed }) => (pressed ? styles.pressed : undefined)}
            testID={testID}
        >
            {line}
        </Pressable>
    )
}

const styles = StyleSheet.create(() => ({
    body: { flex: 1, minWidth: 0 },
    bar: { flex: 1 },
    pressed: { opacity: 0.7 },
}))
